import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { PaperPlaneTilt, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STARTERS = [
  "What should I wear to a Tuesday brunch?",
  "Help me style my favorite blazer.",
  "I have a wedding next weekend — ideas?",
  "Pick me a cozy but cute WFH outfit.",
];

export default function Chat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    const s = typeof window !== "undefined" ? localStorage.getItem("stylist_chat_session") : null;
    return s || null;
  });
  const scrollRef = useRef(null);

  const loadHistory = async (sid) => {
    if (!sid) return;
    try {
      const { data } = await api.get("/chat/history", { params: { session_id: sid } });
      setMessages(data);
    } catch {}
  };
  useEffect(() => { if (sessionId) loadHistory(sessionId); }, [sessionId]);
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const tempUser = {
      id: `tmp-${Date.now()}`, role: "user", content: msg,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, tempUser]);
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", {
        message: msg, session_id: sessionId,
      });
      if (!sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("stylist_chat_session", data.session_id);
      }
      setMessages((m) => {
        const filtered = m.filter((x) => x.id !== tempUser.id);
        return [...filtered, data.user_message, data.ai_message];
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Ava couldn't respond");
      setMessages((m) => m.filter((x) => x.id !== tempUser.id));
    } finally { setSending(false); }
  };

  const newChat = () => {
    setMessages([]); setSessionId(null);
    localStorage.removeItem("stylist_chat_session");
  };

  return (
    <AppShell>
      <div className="flex flex-col h-[calc(100dvh-10rem)]">
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="text-[0.7rem] tracking-[0.3em] uppercase text-charcoal-500">Chat</p>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-charcoal-900 tracking-tight">Talk to Ava</h1>
          </div>
          <button onClick={newChat} data-testid="new-chat-button" className="btn-outline-pill text-xs py-2 px-4">+ New chat</button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto rounded-3xl border border-cream-200 bg-cream-50 p-5 sm:p-8 space-y-5" data-testid="chat-messages">
          {messages.length === 0 && !sending && (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Sparkle size={36} weight="thin" className="text-charcoal-700" />
              <h3 className="font-serif text-3xl mt-4">Hey, I'm Ava.</h3>
              <p className="text-charcoal-600 mt-3 max-w-md">Your stylist in your pocket. Ask me anything — what to wear today, how to style something, or what's missing in your closet.</p>
              <div className="mt-8 flex flex-wrap gap-3 justify-center max-w-2xl">
                {STARTERS.map((s) => (
                  <button
                    key={s} onClick={() => send(s)}
                    data-testid={`starter-${s.slice(0, 10)}`}
                    className="rounded-full border border-cream-200 bg-white px-4 py-2 text-sm text-charcoal-700 hover:-translate-y-0.5 transition-all"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} role={m.role} content={m.content} />
          ))}
          {sending && (
            <div className="flex gap-3 items-end">
              <div className="w-9 h-9 rounded-full bg-accent-lavender flex items-center justify-center flex-shrink-0">
                <Sparkle size={16} weight="fill" />
              </div>
              <div className="rounded-2xl rounded-tl-sm bg-secondary text-charcoal-800 px-5 py-3">
                <span className="typing-dot"></span><span className="typing-dot"></span><span className="typing-dot"></span>
              </div>
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="mt-4 flex items-center gap-3 rounded-full border border-cream-200 bg-cream-50 px-5 py-2"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Tell Ava about the vibe, the event, the mood…"
            data-testid="chat-input"
            className="flex-1 bg-transparent border-none focus:outline-none py-3 text-base"
          />
          <button
            type="submit" disabled={!input.trim() || sending}
            data-testid="chat-send-button"
            className="rounded-full bg-charcoal-700 text-white w-11 h-11 flex items-center justify-center disabled:opacity-40 hover:bg-charcoal-800 transition-all"
            aria-label="Send"
          >
            <PaperPlaneTilt size={16} weight="fill" />
          </button>
        </form>
      </div>
    </AppShell>
  );
}

function MessageBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 items-end ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="w-9 h-9 rounded-full bg-accent-lavender flex items-center justify-center flex-shrink-0">
          <Sparkle size={16} weight="fill" />
        </div>
      )}
      <div
        className={`px-5 py-3 max-w-[85%] sm:max-w-[70%] whitespace-pre-wrap leading-relaxed text-[15px] ${
          isUser
            ? "bg-charcoal-700 text-white rounded-2xl rounded-tr-sm"
            : "bg-secondary text-charcoal-800 rounded-2xl rounded-tl-sm"
        }`}
      >
        {content}
      </div>
    </div>
  );
}
